import { computed, reactive, ref, unref } from 'vue';
import { useRoute } from 'vue-router';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert, useTracking } from 'dashboard/composables';
import {
  OWNER_CONTACT_REASON_MESSAGES,
  OWNER_CONTACT_REASONS,
  OWNER_CONTACT_TRACKING_EVENTS,
} from '../utils/imovelShowConstants';
import { actionErrorMessage } from '../utils/imovelShowFormatters';

/**
 * Revelação auditada do contato do proprietário (termos → motivo → contato).
 *
 * @param {object} options
 * @param {import('vue').Ref<object>} options.imovel
 * @param {import('vue').Ref<string>} options.ownerBusinessContext
 * @param {import('vue').Ref<string>} options.companyName
 * @param {import('vue').Ref<string>} options.agentName
 * @param {import('vue').Ref<string>} options.addressLabel
 * @param {import('vue').Ref<string>} options.reportLink
 */
export function useImovelOwnerContact({
  imovel,
  ownerBusinessContext,
  companyName,
  agentName,
  addressLabel,
  reportLink,
}) {
  const route = useRoute();
  const { track } = useTracking();

  const ownerContactStats = ref(null);
  const ownerContact = ref(null);
  const ownerContactError = ref('');
  const ownerContactLoading = ref(false);
  const ownerRevealLoading = ref(false);
  const showOwnerContactDrawer = ref(false);
  const ownerContactStep = ref('terms');
  const ownerRevealForm = reactive({
    reason: 'VISIT_SCHEDULING',
    termsAccepted: false,
  });

  const ownerSelectedReasonLabel = computed(
    () =>
      OWNER_CONTACT_REASONS.find(
        reason => reason.value === ownerRevealForm.reason
      )?.label || 'conduzir o atendimento'
  );
  const ownerSelectedReasonMessage = computed(
    () =>
      OWNER_CONTACT_REASON_MESSAGES[ownerRevealForm.reason] ||
      ownerSelectedReasonLabel.value.toLowerCase()
  );
  const ownerWeeklyCount = computed(
    () =>
      ownerContactStats.value?.weeklyCount ??
      ownerContactStats.value?.weekly_count ??
      0
  );
  const ownerWeeklyLimit = computed(
    () =>
      ownerContactStats.value?.weeklyLimit ??
      ownerContactStats.value?.weekly_limit ??
      10
  );
  const ownerWeeklyCountLabel = computed(() => {
    if (ownerWeeklyCount.value === 0) {
      return 'Você ainda não solicitou contatos de proprietários esta semana.';
    }
    if (ownerWeeklyCount.value === 1) {
      return 'Você solicitou 1 contato de proprietário esta semana.';
    }

    return `Você solicitou ${ownerWeeklyCount.value} contatos de proprietários esta semana.`;
  });
  const ownerLimitReached = computed(
    () =>
      ownerContactStats.value?.hasReachedLimit ??
      ownerContactStats.value?.has_reached_limit ??
      false
  );
  const ownerContactPhone = computed(
    () => ownerContact.value?.phoneNumber || ownerContact.value?.phone_number
  );
  const ownerContactEmail = computed(() => ownerContact.value?.email);
  const ownerPhoneDigits = computed(() =>
    ownerContactPhone.value?.toString().replace(/\D/g, '')
  );
  const ownerContactWhatsappMessage = computed(
    () =>
      `Olá, ${ownerContact.value?.name || 'proprietário'}! Esta é uma mensagem enviada por um parceiro verificado do ${unref(companyName)}. ` +
      `Eu sou ${unref(agentName)} e estou conduzindo um atendimento sobre ${unref(imovel)?.titulo || 'seu imóvel'}, localizado em ${unref(addressLabel)}. ` +
      `Preciso ${ownerSelectedReasonMessage.value}. Link do anúncio: ${unref(reportLink)}`
  );
  const ownerWhatsappHref = computed(() =>
    ownerPhoneDigits.value
      ? `https://wa.me/${ownerPhoneDigits.value}?text=${encodeURIComponent(
          ownerContactWhatsappMessage.value
        )}`
      : null
  );
  const ownerPhoneHref = computed(() =>
    ownerContactPhone.value ? `tel:${ownerContactPhone.value}` : null
  );
  const ownerContactRequestHash = computed(
    () =>
      ownerContactStats.value?.requestHash ||
      ownerContactStats.value?.request_hash ||
      ''
  );
  const ownerContactLogId = computed(
    () => ownerContactStats.value?.logId || ownerContactStats.value?.log_id
  );

  const fetchOwnerContactStats = async () => {
    if (!route.params.id) return;

    ownerContactLoading.value = true;
    ownerContactError.value = '';
    try {
      const { data } = await ImovelAPI.ownerContactWeeklyCount(route.params.id);
      ownerContactStats.value = data?.payload || data || null;
    } catch (error) {
      ownerContactError.value = actionErrorMessage(
        error,
        'Não foi possível carregar o limite semanal.'
      );
    } finally {
      ownerContactLoading.value = false;
    }
  };

  const ownerContactTrackingPayload = (properties = {}) => ({
    imovel_id: route.params.id,
    owner_contact_step: ownerContactStep.value,
    reason: ownerRevealForm.reason,
    business_context: unref(ownerBusinessContext),
    ...properties,
  });

  const trackOwnerContactEvent = (event, properties = {}) => {
    try {
      track(event, ownerContactTrackingPayload(properties));
    } catch {
      // Tracking should not block owner contact operations.
    }
  };

  const revealOwnerContact = async () => {
    ownerContactError.value = '';

    if (!ownerRevealForm.termsAccepted) {
      ownerContactError.value =
        'Aceite o termo de uso antes de revelar os dados do proprietário.';
      return;
    }

    ownerRevealLoading.value = true;
    try {
      trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.request);
      const { data } = await ImovelAPI.revealOwnerContact(
        route.params.id,
        {
          owner_contact: {
            reason: ownerRevealForm.reason,
            business_context: unref(ownerBusinessContext),
            terms_accepted: ownerRevealForm.termsAccepted,
          },
        },
        {
          hash: ownerContactRequestHash.value,
        }
      );
      const payload = data?.payload || {};
      ownerContactStats.value = payload;
      ownerContact.value = payload.owner || null;
      ownerContactStep.value = 'contact';
      trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.successViewed, {
        owner_contact_log_id: ownerContactLogId.value,
      });
      trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.infoViewed, {
        owner_contact_log_id: ownerContactLogId.value,
      });
      useAlert('Contato do proprietário revelado com auditoria registrada.');
    } catch (error) {
      ownerContactError.value = actionErrorMessage(
        error,
        'Não foi possível revelar o proprietário.'
      );
    } finally {
      ownerRevealLoading.value = false;
    }
  };

  const openOwnerContactDrawer = async () => {
    ownerContactError.value = '';
    ownerContactStep.value = ownerContact.value ? 'contact' : 'terms';
    showOwnerContactDrawer.value = true;
    trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.open);
    trackOwnerContactEvent(
      ownerContact.value
        ? OWNER_CONTACT_TRACKING_EVENTS.infoViewed
        : OWNER_CONTACT_TRACKING_EVENTS.termsViewed
    );
    if (!ownerContactStats.value) {
      await fetchOwnerContactStats();
    }
  };

  const closeOwnerContactDrawer = () => {
    trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.close);
    showOwnerContactDrawer.value = false;
  };

  const acceptOwnerContactTerms = () => {
    ownerRevealForm.termsAccepted = true;
    ownerContactStep.value = 'reason';
    trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.acceptContinue);
    trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.reasonViewed);
  };

  const backOwnerContactStep = () => {
    ownerContactError.value = '';
    if (ownerContactStep.value === 'reason') {
      ownerContactStep.value = 'terms';
      trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.termsViewed);
      return;
    }
    closeOwnerContactDrawer();
  };

  const selectOwnerContactReason = reason => {
    ownerRevealForm.reason = reason.value;
    trackOwnerContactEvent(OWNER_CONTACT_TRACKING_EVENTS.reasonOption, {
      selected_reason: reason.value,
    });
  };

  const trackOwnerContactChannel = channel => {
    const event =
      {
        whatsapp: OWNER_CONTACT_TRACKING_EVENTS.sendMessage,
        phone: OWNER_CONTACT_TRACKING_EVENTS.phoneCall,
        email: OWNER_CONTACT_TRACKING_EVENTS.email,
      }[channel] || OWNER_CONTACT_TRACKING_EVENTS.infoViewed;
    trackOwnerContactEvent(event, {
      owner_contact_log_id: ownerContactLogId.value,
      channel,
    });
    if (!ownerContactLogId.value) return;

    ImovelAPI.trackOwnerContactChannel(route.params.id, {
      log_id: ownerContactLogId.value,
      channel,
    }).catch(() => {});
  };

  return {
    ownerContact,
    ownerContactError,
    ownerContactLoading,
    ownerRevealLoading,
    showOwnerContactDrawer,
    ownerContactStep,
    ownerRevealForm,
    ownerWeeklyCount,
    ownerWeeklyLimit,
    ownerWeeklyCountLabel,
    ownerLimitReached,
    ownerContactPhone,
    ownerContactEmail,
    ownerContactWhatsappMessage,
    ownerWhatsappHref,
    ownerPhoneHref,
    fetchOwnerContactStats,
    revealOwnerContact,
    openOwnerContactDrawer,
    closeOwnerContactDrawer,
    acceptOwnerContactTerms,
    backOwnerContactStep,
    selectOwnerContactReason,
    trackOwnerContactChannel,
  };
}

export default useImovelOwnerContact;
